import { GroupNode } from "@/models/groupNode";

export interface SortTableKey<T extends object> {
  key: keyof T;
  order?: boolean | "asc" | "desc";
}

export interface Sort {
  key: string;
  order?: boolean | "asc" | "desc";
}

const monthOrder = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];
const customOrder = ["WMAZ", "YMAZ", "CORN", "WEAT", "SUNS", "SOYA"];

function isGroupNode<T>(item: any): item is GroupNode<T> {
  return item && "items" in item && item.key !== null && item.value !== null;
}

function inCustomOrder(value: string): boolean {
  return customOrder.indexOf(value) !== -1;
}

function getMonthAndYear(contractDate: string): { month: string; year: number } {
  const month = contractDate.slice(0, 3);
  const year = parseInt(contractDate.slice(3));
  return { month, year };
}

export function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((acc: any, part: string) => acc && acc[part], obj);
}

export function sortByInstrument(a: string, b: string): number {
  const aInCustom = inCustomOrder(a as string);
  const bInCustom = inCustomOrder(b as string);

  if (aInCustom && bInCustom) {
    const orderA = customOrder.indexOf(a as string);
    const orderB = customOrder.indexOf(b as string);
    return orderA - orderB;
  } else if (aInCustom) {
    return -1;
  } else if (bInCustom) {
    return 1;
  } else {
    return (a as string).localeCompare(b as string);
  }
}

export function sortByContractDate(
  a: string,
  b: string,
  order?: "asc" | "desc" | boolean
): number {
  if (!b || !a) return 0;
  const aMY = getMonthAndYear(a as string);
  const bMY = getMonthAndYear(b as string);

  if (aMY.year < bMY.year)
    return order === "asc" || order === true || !order ? -1 : 1;
  if (aMY.year > bMY.year)
    return order === "asc" || order === true || !order ? 1 : -1;

  const aMonthIndex = monthOrder.indexOf(aMY.month);
  const bMonthIndex = monthOrder.indexOf(bMY.month);

  if (aMonthIndex < bMonthIndex)
    return order === "asc" || order == true || !order ? -1 : 1;
  if (aMonthIndex > bMonthIndex)
    return order === "asc" || order == true || !order ? 1 : -1;

  return 0;
}

export function sortItems<T extends object>(items: T[], sortBy: SortTableKey<T>[]): T[] {
  return items.sort((a, b) => {
    const aInstr = getNestedValue(a, "contractDisplay.instrument");
    const bInstr = getNestedValue(b, "contractDisplay.instrument");
    const aInstrFlat = a["instrument" as keyof T];
    const bInstrFlat = b["instrument" as keyof T];
    if (!aInstr && !aInstrFlat) return 0;
    const result = sortByInstrument(aInstr || aInstrFlat, bInstr || bInstrFlat);
    if (result !== 0) return result;

    for (const sortObj of sortBy) {
      const { key, order } = sortObj;
      if ((key as string).endsWith("contractDate")) {
        const aValue = getNestedValue(a, key as string);
        const bValue = getNestedValue(b, key as string);
        const r = sortByContractDate(aValue, bValue, order);
        if (r !== 0) return r;
      } else if ((key as string).endsWith("instrument")) {
        continue;
      } else if ((key as string).endsWith("strike")) {
        const aValue = getNestedValue(a, key as string);
        const bValue = getNestedValue(b, key as string);
        return order === "desc" ? bValue - aValue : aValue - bValue;
      } else {
        if (a[key] < b[key]) return order === "asc" ? -1 : 1;
        if (a[key] > b[key]) return order === "asc" ? 1 : -1;
      }
    }
    return 0;
  });
}

export function group<T extends object>(
  items: T[],
  groupBy: (keyof T)[],
  sortBy: SortTableKey<T>[],
  isOpenStates: Record<string, any>,
  parentKey: string = ""
): GroupNode<T>[] | T[] {
  if (groupBy.length === 0) {
    return sortItems(items, sortBy);
  }
  items = sortItems(items, sortBy);
  const result: GroupNode<T>[] = [];
  const temp = groupBy[0].toString().includes(".");
  const gValue = groupBy[0].toString();
  const fieldPath = temp == true ? gValue.split(".") : [gValue];

  const grouped = items.reduce((grouped: Record<string, T[]>, item: T) => {
    let fieldValue: any = item;
    for (const keyPart of fieldPath) {
      fieldValue = fieldValue[keyPart];
      if (fieldValue === undefined) break;
    }
    const key =
      (parentKey ? parentKey + "|" : "") +
      (fieldValue?.toString() || "default");
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
    return grouped;
  }, {});

  for (const key in grouped) {
    let obj: any = grouped[key][0];
    for (const keyPart of fieldPath) {
      if (obj[keyPart] !== undefined) {
        obj = obj[keyPart];
      } else {
        obj = undefined;
        break;
      }
    }
    const fieldValue = obj?.toString() || null;
    const currentIsOpen =
      isOpenStates[key] !== undefined ? isOpenStates[key] : false;

    if (groupBy.length === 1) {
      const groupKey = fieldPath[fieldPath.length - 1] as keyof T;
      result.push({
        key,
        value: fieldValue,
        items: sortItems(grouped[key], sortBy),
        isOpen: currentIsOpen,
        groupKey,
      });
    } else {
      let childItems = group(
        grouped[key],
        groupBy.slice(1),
        sortBy,
        isOpenStates,
        key
      );
      if (
        Array.isArray(childItems) &&
        childItems.length > 0 &&
        !isGroupNode(childItems[0])
      ) {
        childItems = sortItems(childItems as T[], sortBy);
      } else {
        for (const groupItem of childItems as GroupNode<T>[]) {
          if (!isGroupNode(groupItem.items[0])) {
            groupItem.items = sortItems(groupItem.items as T[], sortBy);
          }
        }
      }
      result.push({
        key,
        value: fieldValue,
        items: Array.isArray(childItems) ? childItems : [childItems],
        isOpen: currentIsOpen,
        groupKey: fieldPath[fieldPath.length - 1] as keyof T,
      });
    }
  }

  if (
    result[0] &&
    result[0].groupKey != "instrument" &&
    result[0].groupKey != "contractDisplay" &&
    result[0].groupKey != "contractDate"
  ) {
    result.sort((a, b) => {
      if (a.key == null || b.key == null) return 0;
      if (a.key < b.key) return -1;
      if (a.key > b.key) return 1;
      return 0;
    });
  } else if (result[0] && result[0].groupKey == "contractDate") {
    result.sort((a, b) =>
      sortByContractDate(a.key as string, b.key as string)
    );
  }

  return result.length > 0 ? result : items;
}
